import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import logoOrig from '../img/tmis_logo.png';
import logoWhite from '../img/tmis_logo_w.png';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

const NAV_ITEMS = [
  { label: 'Home',                to: '/student',             end: true, icon: 'bi-speedometer2' },
  { label: 'Attendance',          to: '/student/attendance',             icon: 'bi-calendar-check' },
  { label: 'Academic Performance', to: '/student/academic',              icon: 'bi-graph-up' },
  { label: 'Activities',          to: '/student/activities',             icon: 'bi-activity' },
  { label: 'My Requests',         to: '/student/requests',               icon: 'bi-envelope-open' },
  { label: 'My Profile',          to: '/student/profile',                icon: 'bi-person-badge' },
];

function initials(name = '') {
  return name.split(' ').slice(0, 2).map(w => w[0] || '').join('').toUpperCase();
}

export default function StudentLayout({ children }) {
  const { user, avatarColor, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate          = useNavigate();
  const location          = useLocation();
  const isDark             = theme === 'dark';
  const [pictureUrl, setPictureUrl] = useState(null);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('sms_student_rail_collapsed') === '1');
  const toggleCollapsed = () => {
    setCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('sms_student_rail_collapsed', next ? '1' : '0');
      return next;
    });
  };

  useEffect(() => {
    if (!user?.profile_picture || !user?.student_no) { setPictureUrl(null); return; }
    let cancelled = false;
    let objectUrl = null;
    api.get(`/students/${user.student_no}/picture`, { responseType: 'blob' }).then(res => {
      if (cancelled) return;
      objectUrl = URL.createObjectURL(res.data);
      setPictureUrl(objectUrl);
    }).catch(() => { if (!cancelled) setPictureUrl(null); });
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [user?.profile_picture, user?.student_no]);

  const handleLogout = async () => { await logout(); navigate('/login'); };

  return (
    <div className="stu-app">
      <aside className={`stu-rail ${collapsed ? 'collapsed' : ''}`} aria-label="Student portal navigation">
        <div className="stu-rail-head">
          <div className="stu-brand">
            <img src={isDark ? logoWhite : logoOrig} alt="TMIS SMS" style={{ height: 26, width: 'auto' }} />
          </div>
          <button
            className="stu-collapse-btn"
            onClick={toggleCollapsed}
            aria-pressed={collapsed}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <i className="bi bi-chevron-bar-left" aria-hidden="true" />
          </button>
        </div>

        <nav className="stu-nav" aria-label="Primary">
          {NAV_ITEMS.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-label={item.label}
              className={({ isActive }) => `stu-nav-item ${isActive ? 'active' : ''}`}
            >
              <i className={`bi ${item.icon}`} aria-hidden="true" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="stu-rail-foot">
          <div className="stu-user">
            <div className="stu-avatar" style={pictureUrl ? { padding: 0, overflow: 'hidden' } : { background: avatarColor || '#1d46c8', color: '#ffffff' }}>
              {pictureUrl
                ? <img src={pictureUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : initials(user?.full_name)}
            </div>
            <div>
              <div className="stu-user-name">{user?.full_name}</div>
              <div className="stu-user-id">{user?.student_no}</div>
            </div>
          </div>
          <div className="stu-rail-actions">
            <button className="sms-btn sms-btn-outline" onClick={toggle}
              title={isDark ? 'Light mode' : 'Dark mode'} aria-label={isDark ? 'Light mode' : 'Dark mode'}>
              <i className={`bi ${isDark ? 'bi-sun' : 'bi-moon'}`} aria-hidden="true" />
              <span className="sms-btn-label">{isDark ? 'Light mode' : 'Dark mode'}</span>
            </button>
            <button className="sms-btn sms-btn-danger" onClick={handleLogout} title="Log out" aria-label="Log out">
              <i className="bi bi-box-arrow-right" aria-hidden="true" />
              <span className="sms-btn-label">Log out</span>
            </button>
          </div>
        </div>

        <div className="stu-rail-mobile-actions">
          <button className="stu-icon-btn" onClick={toggle} aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}>
            <i className={`bi ${isDark ? 'bi-sun' : 'bi-moon'}`} aria-hidden="true" />
          </button>
          <button className="stu-icon-btn" onClick={handleLogout} aria-label="Log out">
            <i className="bi bi-box-arrow-right" aria-hidden="true" />
          </button>
        </div>
      </aside>

      <div className="stu-main">
        <main id="main-content" className="stu-main-content" tabIndex="-1" key={location.pathname}>
          {children}
        </main>
      </div>

      <nav className="stu-bottom-nav" aria-label="Primary mobile navigation">
        {NAV_ITEMS.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) => `stu-bottom-nav-item ${isActive ? 'active' : ''}`}
          >
            <i className={`bi ${item.icon}`} aria-hidden="true" />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
